import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Sparkle, ArrowRight, Trophy, Calendar, Rocket, House } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { useWorkflowStore } from '@/store/workflowStore';
import useWorkflowProgress from '@/hooks/useWorkflowProgress';

/**
 * BusinessCompletionState — Round 218A Phase 2
 *
 * Guidance-only completion screen. Shown when the workflow store
 * signals the essentials (offer, sales page, content) are done.
 * The card RECOMMENDS the next canonical workflow step but never
 * forces navigation. Customers can always Continue, Return to
 * Dashboard, or open any other builder.
 */
const BusinessCompletionState = ({ onPlanWeek, onViewCreations }) => {
  const navigate = useNavigate();
  const { offerCreated, salesPageCreated, contentCreated, offerName } = useWorkflowStore();
  const { nextStep: workflowNextStep } = useWorkflowProgress();

  const isBusinessReady = offerCreated && salesPageCreated && contentCreated;
  if (!isBusinessReady) return null;

  // Prefer the canonical workflow's next step; fall back to Plan My
  // Week (the legacy default) when the workflow says there's nothing
  // outstanding.
  const nextLabel = workflowNextStep?.label || 'Plan My Week';
  const nextRoute = workflowNextStep?.route || null;

  const handleContinue = () => {
    if (nextRoute) {
      navigate(nextRoute);
    } else if (onPlanWeek) {
      onPlanWeek();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-emerald-500/20 via-emerald-600/10 to-teal-500/20 border border-emerald-500/30 rounded-2xl p-6 mb-6"
      data-testid="business-completion-state"
    >
      {/* Success Header */}
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
          <Trophy size={24} className="text-white" weight="fill" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white flex items-center space-x-2">
            <span>Your Business is Ready!</span>
            <Sparkle size={20} className="text-yellow-400" weight="fill" />
          </h2>
          <p className="text-emerald-400 text-sm">You&apos;ve built the foundation. Time to grow.</p>
        </div>
      </div>

      {/* Completed Items */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="flex items-center space-x-2 bg-white/5 rounded-lg p-3">
          <CheckCircle size={18} className="text-emerald-400" weight="fill" />
          <span className="text-slate-300 text-sm">Offer Created</span>
        </div>
        <div className="flex items-center space-x-2 bg-white/5 rounded-lg p-3">
          <CheckCircle size={18} className="text-emerald-400" weight="fill" />
          <span className="text-slate-300 text-sm">Sales Page Ready</span>
        </div>
        <div className="flex items-center space-x-2 bg-white/5 rounded-lg p-3">
          <CheckCircle size={18} className="text-emerald-400" weight="fill" />
          <span className="text-slate-300 text-sm">Content Created</span>
        </div>
      </div>

      {/* Offer Name Badge */}
      {offerName && (
        <div className="mb-4 p-3 bg-white/5 rounded-lg">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Your Offer</p>
          <p className="text-white font-medium">{offerName}</p>
        </div>
      )}

      {/* Recommended Next Step — guidance only */}
      <div className="space-y-3">
        <p className="text-slate-300 text-sm" data-testid="completion-next-step-hint">
          <span className="text-emerald-400 font-semibold">We recommend:</span>{' '}
          {workflowNextStep
            ? `Continue with ${nextLabel} to keep momentum.`
            : 'Plan your content week to start driving traffic.'}
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleContinue}
            data-testid="completion-continue-btn"
            className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
          >
            <Calendar size={18} weight="fill" />
            <span>Continue to {nextLabel}</span>
            <ArrowRight size={16} weight="bold" />
          </button>

          <button
            onClick={() => navigate('/dashboard')}
            data-testid="completion-dashboard-btn"
            className="py-3 px-6 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
          >
            <House size={18} />
            <span>Back to Dashboard</span>
          </button>

          <button
            onClick={onViewCreations}
            data-testid="view-creations-cta"
            className="py-3 px-6 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
          >
            <Rocket size={18} />
            <span>My Creations</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default BusinessCompletionState;
